import React, { useState, useEffect } from 'react';
import styles from './DynamicEntityForm.module.css';
import { fetchEntityConfig } from '../../services/workbenchDataService';

const DynamicEntityForm = ({ config, data, entityKey = null, configBasePath = '', isSubEntity = false }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [subEntityConfigs, setSubEntityConfigs] = useState({});

  const pageConfig = config?.page?.[0];
  const groupings = pageConfig?.grouping || [];
  const attributes = config?.attributes || [];

  // Load sub-entity configs dynamically from API
  useEffect(() => {
    const loadSubEntityConfigs = async () => {
      // Filter attributes that are sub-entities (type: 'SUBENTITY' or 'sub_entity')
      const subEntityAttrs = attributes.filter(attr => 
        (attr.type === 'SUBENTITY' || attr.type === 'sub_entity') && attr.entity_cd
      );
      const configs = {};

      for (const attr of subEntityAttrs) {
        try {
          // Fetch sub-entity config from API using entity_cd
          const subConfig = await fetchEntityConfig(attr.entity_cd);
          configs[attr.key] = subConfig;
        } catch (error) {
          console.error(`Failed to load sub-entity config for ${attr.key} (${attr.entity_cd}):`, error);
        }
      }

      setSubEntityConfigs(configs);
    };

    if (attributes.length > 0) {
      loadSubEntityConfigs();
    }
  }, [attributes]);

  if (!config || !config.page || !config.page[0]) {
    return <div>No configuration available</div>;
  }

  // Toggle section collapse/expand
  const toggleSection = (groupCode) => {
    setExpandedSections(prev => ({
      ...prev,
      [groupCode]: !prev[groupCode]
    }));
  };

  // Get value from data object, handling nested paths
  const getValue = (key, dataObj = data) => {
    if (!dataObj || !key) return null;
    
    // Handle nested keys like "event.eventType"
    if (key.includes('.')) {
      const parts = key.split('.');
      let value = dataObj;
      for (const part of parts) {
        value = value?.[part];
      }
      return value;
    }
    
    return dataObj[key];
  };

  // Format value based on field type
  const formatValue = (attribute, value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    // Handle objects and arrays - convert to JSON string
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return `[${value.length} items]`;
      }
      return '[Object]';
    }

    switch (attribute.type) {
      case 'DATE':
        return value;
      case 'NUMBER':
        return typeof value === 'number' ? value.toLocaleString() : value;
      case 'BOOLEAN':
        return value ? 'Yes' : 'No';
      default:
        return String(value);
    }
  };

  // Render field based on type
  const renderField = (attribute) => {
    const value = getValue(attribute.key);
    const formattedValue = formatValue(attribute, value);
    const isDisabled = attribute.ui_properties?.disabled;

    return (
      <div key={attribute.key} className={styles.fieldGroup}>
        <label className={styles.fieldLabel}>
          {attribute.label}
          {attribute.mandatory && <span className={styles.required}>*</span>}
        </label>
        <div className={styles.fieldValue}>
          {isDisabled ? (
            <span className={styles.readOnlyValue}>{formattedValue}</span>
          ) : (
            <input
              type="text"
              className={styles.fieldInput}
              value={formattedValue}
              placeholder={attribute.ui_properties?.placeholder || ''}
              disabled={isDisabled}
            />
          )}
        </div>
      </div>
    );
  };

  // Render sub-entity (nested object)
  const renderSubEntity = (attribute) => {
    const subEntityData = getValue(attribute.key);
    
    if (!subEntityData) {
      return null;
    }

    const subConfig = subEntityConfigs[attribute.key];
    
    if (!subConfig) {
      return (
        <div key={attribute.key} className={styles.subEntityPlaceholder}>
          <div className={styles.subEntityNote}>
            Loading {attribute.label}...
          </div>
        </div>
      );
    }

    // Check if this is a list of sub-entities
    const isList = attribute.islist === true || Array.isArray(subEntityData);

    if (isList && Array.isArray(subEntityData)) {
      return (
        <div key={attribute.key} className={styles.subEntityList}>
          <div className={styles.subEntityListHeader}>{attribute.label}</div>
          {subEntityData.map((item, index) => (
            <div key={index} className={styles.subEntityListItem}>
              <DynamicEntityForm
                config={subConfig}
                data={item}
                entityKey={`${attribute.key}[${index}]`}
                configBasePath={configBasePath}
              />
            </div>
          ))}
        </div>
      );
    }

    // Single sub-entity
    return (
      <div key={attribute.key} className={styles.subEntityContainer}>
        <DynamicEntityForm
          config={subConfig}
          data={subEntityData}
          entityKey={attribute.key}
          configBasePath={configBasePath}
          isSubEntity={true}
        />
      </div>
    );
  };

  // Render section content
  const renderSectionContent = (groupCode) => {
    const groupAttributes = attributes
      .filter(attr => attr.groupCode === groupCode)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    if (groupAttributes.length === 0) {
      return <div className={styles.noFields}>No fields available</div>;
    }

    return (
      <div className={styles.fieldsContainer}>
        {groupAttributes.map(attribute => {
          if (attribute.ui_properties?.hidden) {
            return null;
          }

          if (attribute.type === 'SUBENTITY' || attribute.type === 'sub_entity') {
            return renderSubEntity(attribute);
          }

          return renderField(attribute);
        })}
      </div>
    );
  };

  // If this is a single sub-entity, render content directly without section headers
  if (isSubEntity) {
    return (
      <div className={styles.formContainer}>
        {groupings.map((group) => (
          <div key={group.groupCode} className={styles.sectionContent}>
            {renderSectionContent(group.groupCode)}
          </div>
        ))}
      </div>
    );
  }

  // Regular rendering with section headers
  return (
    <div className={styles.formContainer}>
      {groupings.map((group) => {
        const isExpanded = expandedSections[group.groupCode] !== false; // Default to expanded

        return (
          <div key={group.groupCode} className={styles.section}>
            <div 
              className={styles.sectionHeader}
              onClick={() => toggleSection(group.groupCode)}
            >
              <div className={styles.sectionHeaderContent}>
                <h3 className={styles.sectionTitle}>{group.title}</h3>
                {group.subtitle && (
                  <span className={styles.sectionSubtitle}>{group.subtitle}</span>
                )}
              </div>
              <button className={styles.collapseButton}>
                {isExpanded ? '−' : '+'}
              </button>
            </div>

            {isExpanded && (
              <div className={styles.sectionContent}>
                {renderSectionContent(group.groupCode)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DynamicEntityForm;
